'use client'

import React, { useEffect, useState } from 'react'
import { Filter, ChevronDown, XCircle } from 'lucide-react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { JOB_CATEGORIES } from '@/constants/job-options'

export type JobFilterType = 'project' | 'team' | 'maker'

type Chip = {
  key: string
  label: string
  tone?: 'blue' | 'gray'
  onClear: () => void
}

export function FilterChipRow({ chips }: { chips: Chip[] }) {
  if (!chips || chips.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map((c) => {
        const tone =
          c.tone === 'blue'
            ? 'bg-blue-100 text-blue-700'
            : 'bg-gray-100 text-gray-700'
        return (
          <span
            key={c.key}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium inline-flex items-center gap-2 ${tone}`}
          >
            {c.label}
            <button
              type="button"
              onClick={c.onClear}
              className="text-xs text-gray-600 hover:text-gray-900"
              aria-label={`${c.label} 제거`}
            >
              ×
            </button>
          </span>
        )
      })}
    </div>
  )
}

export function FilterMoreButton({
  expanded,
  onToggle,
  label = '필터 더보기',
  compact = false,
}: {
  expanded: boolean
  onToggle: () => void
  label?: string
  compact?: boolean
}) {
  if (compact) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
          expanded
            ? 'border-blue-500 text-blue-600 bg-blue-50'
            : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-700'
        }`}
      >
        {label}
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-all text-sm font-medium text-gray-700"
    >
      <span>{label}</span>
      <span
        className={`text-sm transition-transform ${expanded ? 'transform rotate-180' : ''}`}
        aria-hidden
      >
        ▾
      </span>
    </button>
  )
}

export function FilterPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="space-y-4">{children}</div>
    </div>
  )
}

export function FilterSectionHeader({ title = '필터' }: { title?: string }) {
  return (
    <div className="flex items-center gap-2">
      <Filter className="w-4 h-4 text-gray-600" />
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
    </div>
  )
}

export function FilterSection({ 
  children, 
  title = '필터' 
}: { 
  children: React.ReactNode
  title?: string 
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="space-y-4">
        <FilterSectionHeader title={title} />
        <div className="flex flex-wrap gap-3">{children}</div>
      </div>
    </div>
  )
}

export function SingleSelectPills({
  items,
  selected,
  onSelect,
  allLabel = '전체',
}: {
  items: string[]
  selected: string | null
  onSelect: (v: string | null) => void
  allLabel?: string
}) {
  return (
    <div className="flex flex-wrap gap-3">
      <button
        type="button"
        onClick={() => onSelect(null)}
        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
          !selected
            ? 'bg-blue-600 text-white'
            : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
        }`}
      >
        {allLabel}
      </button>

      {items.map((it) => {
        const isOn = selected === it
        return (
          <button
            key={it}
            type="button"
            onClick={() => onSelect(it)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              isOn
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
            }`}
          >
            {it}
          </button>
        )
      })}
    </div>
  )
}

/**
 * 다중 선택을 지원하는 pills 컴포넌트
 */
export function MultiSelectPills({
  items,
  selected,
  onSelect,
  onDeselect,
  maxSelection,
  allLabel = '전체',
}: {
  items: string[]
  selected: string[]
  onSelect: (v: string) => void
  onDeselect: (v: string) => void
  maxSelection?: number
  allLabel?: string
}) {
  const handleToggle = (item: string) => {
    if (selected.includes(item)) {
      onDeselect(item)
    } else {
      if (maxSelection && selected.length >= maxSelection) {
        return
      }
      onSelect(item)
    }
  }

  const handleSelectAll = () => {
    if (selected.length === items.length) {
      // 모두 선택되어 있으면 모두 해제
      selected.forEach(onDeselect)
    } else {
      // 모두 선택
      items.forEach((item) => {
        if (!selected.includes(item)) {
          if (!maxSelection || selected.length < maxSelection) {
            onSelect(item)
          }
        }
      })
    }
  }

  const isAllSelected = selected.length === items.length
  const isSomeSelected = Boolean(selected.length > 0 && selected.length < items.length)

  return (
    <div className="flex flex-wrap gap-3">
      <button
        type="button"
        onClick={handleSelectAll}
        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
          isAllSelected
            ? 'bg-blue-600 text-white'
            : isSomeSelected
              ? 'bg-blue-100 text-blue-700 border border-blue-300'
              : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
        }`}
      >
        {allLabel}
      </button>

      {items.map((it) => {
        const isOn = selected.includes(it)
        const isDisabled = Boolean(!isOn && maxSelection && selected.length >= maxSelection)
        return (
          <button
            key={it}
            type="button"
            onClick={() => handleToggle(it)}
            disabled={isDisabled}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              isOn
                ? 'bg-blue-600 text-white'
                : isDisabled
                  ? 'bg-gray-50 border border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
            }`}
          >
            {it}
          </button>
        )
      })}
    </div>
  )
}

/**
 * 분야/직무 등 주요 필터를 왼쪽에 토글 버튼으로 표시하는 컴포넌트
 */
export function FilterMainToggle({
  label,
  selectedLabel,
  expanded,
  onToggle,
  selected = false,
}: {
  label: string
  selectedLabel?: string
  expanded: boolean
  onToggle: () => void
  selected?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
        selected
          ? 'bg-blue-600 text-white'
          : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
      }`}
    >
      <span>
        {label}: {selectedLabel || '전체'}
      </span>
      <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
    </button>
  )
}

/**
 * 분야/직무 토글을 확장했을 때 보이는 패널
 */
export function FilterMainPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex flex-wrap gap-3">{children}</div>
    </div>
  )
}

/**
 * 왼쪽에 주요 필터 토글, 오른쪽에 필터 더보기를 배치하는 레이아웃 컴포넌트
 */
export function FilterBarRow({
  leftContent,
  rightContent,
}: {
  leftContent: React.ReactNode
  rightContent?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div>{leftContent}</div>
      {rightContent && <div>{rightContent}</div>}
    </div>
  )
}

/**
 * 직무 필터 모달 컴포넌트 (프로젝트/팀/메이커 분리)
 */
export function JobFilterModal({
  value,
  onChange,
  type = 'maker',
  open: controlledOpen,
  onOpenChange,
  title = '직무 선택',
  trigger,
}: {
  value: string[]
  onChange: (value: string[]) => void
  type?: JobFilterType
  open?: boolean
  onOpenChange?: (open: boolean) => void
  title?: string
  trigger?: React.ReactNode
}) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [internalOpen, setInternalOpen] = useState(false)
  const [localValue, setLocalValue] = useState<string[]>(value)

  // controlled 또는 uncontrolled 모드 지원
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen
  const setOpen = onOpenChange || setInternalOpen

  // 타입별 최대 선택 개수
  const maxSelection = type === 'project' ? undefined : 3

  useEffect(() => {
    if (!open) {
      setLocalValue(value)
    }
  }, [value, open])

  const handleToggleJob = (job: string) => {
    if (maxSelection && localValue.length >= maxSelection && !localValue.includes(job)) {
      return
    }
    const newJobs = localValue.includes(job)
      ? localValue.filter((j) => j !== job)
      : [...localValue, job]
    setLocalValue(newJobs)
  }

  const handleApply = () => {
    onChange(localValue)
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="w-[calc(100vw-32px)] max-w-[500px] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="grid grid-cols-[1fr_1.5fr] gap-4 py-4">
            <div className="border-r border-gray-200 pr-4">
              <h4 className="text-sm font-medium mb-3 text-gray-700">분류</h4>
              <div className="space-y-1">
                {JOB_CATEGORIES.map((category) => (
                  <button
                    key={category.category}
                    onClick={() => setSelectedCategory(category.category)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                      selectedCategory === category.category
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {category.category}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium mb-3 text-gray-700">직무</h4>
              {selectedCategory ? (
                <div className="space-y-1">
                  {JOB_CATEGORIES.find((c) => c.category === selectedCategory)
                    ?.jobs.map((job) => {
                      const isSelected = localValue.includes(job)
                      const isDisabled =
                        maxSelection && localValue.length >= maxSelection && !isSelected
                      return (
                        <label
                          key={job}
                          className={`flex items-center justify-between px-3 py-2 text-sm rounded-md hover:bg-gray-50 cursor-pointer ${
                            isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <span className="text-gray-700">{job}</span>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleJob(job)}
                            disabled={isDisabled}
                            className="w-4 h-4"
                          />
                        </label>
                      )
                    })}
                </div>
              ) : (
                <p className="text-sm text-gray-400 py-4">분류를 선택해주세요</p>
              )}
            </div>
          </div>

          {localValue.length > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">
                  선택된 직무 ({localValue.length}
                  {maxSelection ? `/${maxSelection}` : ''})
                </span>
                <button
                  onClick={() => setLocalValue([])}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  전체 삭제
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {localValue.map((job) => (
                  <div
                    key={job}
                    className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-md text-sm"
                  >
                    <span>{job}</span>
                    <XCircle
                      className="h-3 w-3 cursor-pointer text-gray-400 hover:text-gray-600"
                      onClick={() => setLocalValue((prev) => prev.filter((j) => j !== job))}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 border-t border-gray-200 pt-4 mt-4">
          <div className="flex items-center justify-between w-full">
            <DialogClose asChild>
              <Button variant="outline" size="sm">
                취소
              </Button>
            </DialogClose>
            <Button size="sm" onClick={handleApply}>
              적용
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * 경력 필터 컴포넌트 (1~20년 범위)
 */
export function ExperienceFilterPanel({
  value,
  onChange,
}: {
  value: [number, number] | undefined
  onChange: (value: [number, number] | undefined) => void
}) {
  const defaultValue: [number, number] = [0, 20]
  const currentValue = value ?? defaultValue
  const [localValue, setLocalValue] = useState<[number, number]>(currentValue)

  useEffect(() => {
    setLocalValue(value ?? defaultValue)
  }, [value])

  const handleApply = () => {
    // 기본값과 같으면 undefined로 설정 (필터 해제)
    if (localValue[0] === defaultValue[0] && localValue[1] === defaultValue[1]) {
      onChange(undefined)
    } else {
      onChange(localValue)
    }
  }

  const handleReset = () => {
    setLocalValue(defaultValue)
    onChange(undefined)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">경력</h3>
        {value && (
          <button
            type="button"
            onClick={handleReset}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            초기화
          </button>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <Slider
          value={localValue}
          onValueChange={(newValue) => setLocalValue(newValue as [number, number])}
          min={0}
          max={20}
          step={1}
          className="w-full"
        />
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            {localValue[0]}년
          </span>
          <span className="text-gray-900 font-medium">
            {localValue[0]}년 ~ {localValue[1]}년
          </span>
          <span className="text-gray-600">
            {localValue[1]}년
          </span>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
        <Button
          variant="outline"
          size="sm"
          onClick={handleReset}
          className="text-sm"
        >
          취소
        </Button>
        <Button
          size="sm"
          onClick={handleApply}
          className="text-sm"
        >
          적용
        </Button>
      </div>
    </div>
  )
}
